import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check, Plus, FileText, ChevronRight } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import usePageTitle from '../hooks/usePageTitle';
import {
  LAB_PANELS,
  BIOMARKERS,
  SAMPLE_DRAW,
  MARKER_CATEGORIES,
  PROCESS_STEPS,
  TESTIMONIALS,
  BRAND_INFO,
} from '../data/mock';

const HomePage = () => {
  usePageTitle(
    `${BRAND_INFO.name} | Bloodwork rebuilt for clarity`,
    'Order lab panels for testosterone, estradiol, IGF-1 and 140+ more markers, or build your own panel à la carte. Live-streaming results in your Anabolic Surge dashboard.'
  );

  // Auto-cycling "latest draw" preview indicator
  const [previewIdx, setPreviewIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setPreviewIdx((i) => (i + 1) % 4), 2800);
    return () => clearInterval(t);
  }, []);

  return (
    <>
      <Header />

      {/* HERO */}
      <section className="relative pt-28 lg:pt-36 pb-16 lg:pb-24 bg-cream-100 overflow-hidden">
        <div className="container-wide">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
            {/* Headline column */}
            <div className="lg:col-span-6">
              <h1 className="font-display text-display-xl text-ink-900">
                Your blood work, rebuilt for{' '}
                <em className="italic text-brick-500">clarity</em>.
              </h1>

              <div className="mt-9 flex flex-wrap items-center gap-3">
                <Link to="/panels" className="btn-brick">
                  Get started
                </Link>
                <Link to="/biomarkers" className="btn-light">
                  Build à la carte
                </Link>
              </div>
            </div>

            {/* Latest draw card */}
            <div className="lg:col-span-6">
              <div className="bg-cream-50 border border-cream-200 rounded-2xl p-6 lg:p-8 shadow-soft">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-display text-2xl text-ink-900">Your latest draw</h3>
                    <div className="mt-1 text-[11px] tracking-[0.18em] uppercase text-ink-500">
                      Hormone panel · Sample
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-ink-500">
                    <span className="w-1.5 h-1.5 rounded-full bg-sage-500 animate-pulse-soft"></span>
                    Streaming
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  {SAMPLE_DRAW.map((m) => (
                    <div key={m.name} className="grid grid-cols-12 items-center gap-3">
                      <div className="col-span-4 text-[14px] text-ink-800">{m.name}</div>
                      <div className="col-span-6">
                        <div className="relative h-2 range-bar">
                          <span
                            className="absolute top-1/2 -translate-y-1/2 w-[3px] h-4 bg-ink-900 rounded"
                            style={{ left: `calc(${m.pct}% - 1px)` }}
                          ></span>
                        </div>
                      </div>
                      <div className="col-span-2 text-right text-[14px] tabular-nums">
                        <span className="text-ink-900 font-medium">{m.value}</span>{' '}
                        <span className="text-ink-500 text-[12px]">{m.unit}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-7 pt-5 border-t border-cream-200 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    {[0, 1, 2, 3].map((i) => (
                      <span
                        key={i}
                        className={`w-1.5 h-1.5 rounded-full transition-colors ${
                          i === previewIdx ? 'bg-ink-900' : 'bg-cream-300'
                        }`}
                      ></span>
                    ))}
                  </div>
                  <div className="text-[11px] tracking-[0.18em] uppercase text-ink-400">
                    Auto-cycling preview
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* START WITH A PANEL */}
      <section className="py-20 lg:py-28 bg-cream-100">
        <div className="container-wide">
          <div className="max-w-3xl">
            <h2 className="font-display text-display-lg text-ink-900">Start with a panel.</h2>
            <p className="mt-4 text-lg text-ink-600">
              Pick a curated panel or build your own à la carte. Every requisition is
              physician-signed and drawn at a partner Quest, LabCorp, or BioReference
              location near you.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {LAB_PANELS.slice(0, 6).map((p) => (
              <Link
                to="/panels"
                key={p.id}
                className="group relative bg-cream-50 border border-cream-200 rounded-3xl p-7 hover:border-brick-300 hover:shadow-card transition-all"
              >
                <div className="flex items-start justify-between">
                  <h3 className="font-display text-2xl text-ink-900 max-w-[70%] leading-tight">
                    {p.name}
                  </h3>
                  <div className="text-right">
                    <div className="text-[10px] tracking-[0.18em] uppercase text-ink-400">From</div>
                    <div className="font-display text-2xl text-ink-900">${p.price}</div>
                  </div>
                </div>

                <div className="mt-3 text-[10px] tracking-[0.18em] uppercase text-ink-400">
                  {p.badge}
                </div>

                <ul className="mt-5 space-y-2.5 text-[14px] text-ink-700">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5">
                      <span className="mt-1 inline-flex w-3 h-3 rounded bg-sage-300"></span>
                      {b}
                    </li>
                  ))}
                </ul>

                <div className="mt-7 inline-flex items-center gap-2 text-brick-500 group-hover:gap-3 transition-all border border-brick-200 rounded-full px-4 py-2 text-[14px]">
                  View details <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-10 flex justify-center">
            <Link to="/panels" className="btn-light">
              See every panel <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* TIMELINE / DARK SECTION */}
      <section className="relative py-24 lg:py-32 bg-charcoal-900 text-cream-100 overflow-hidden">
        <div className="container-wide">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-6">
              <p className="eyebrow !text-cream-300 [&::before]:!text-cream-300">Your data, over time</p>
              <h2 className="mt-6 font-display text-display-lg text-cream-50">
                Finally see what&apos;s actually{' '}
                <em className="italic text-brick-300">moving</em>.
              </h2>
              <p className="mt-6 text-cream-200/80 text-lg max-w-md">
                Every marker you have ever drawn — plotted on one timeline. Spot the
                inflection points your clinician&apos;s PDF would flatten into a single number.
              </p>
            </div>

            {/* Chart card */}
            <div className="lg:col-span-6">
              <div className="bg-charcoal-800/80 border border-charcoal-700 rounded-2xl p-7">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-display text-2xl text-cream-50">Total Testosterone</h3>
                    <div className="mt-1 text-[11px] tracking-[0.18em] uppercase text-cream-300/70">
                      Last 13 weeks · 7 draws
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-4xl text-cream-50 leading-none">842</div>
                    <div className="mt-1 text-[12px] text-sage-300">+230 ng/dL · +37.6%</div>
                  </div>
                </div>

                {/* SVG line chart */}
                <div className="mt-7 relative h-44">
                  <svg viewBox="0 0 400 160" className="w-full h-full" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="areaGrad" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#74a87f" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="#74a87f" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <g stroke="rgba(245,236,217,0.07)" strokeDasharray="2 5">
                      <line x1="0" y1="40" x2="400" y2="40" />
                      <line x1="0" y1="80" x2="400" y2="80" />
                      <line x1="0" y1="120" x2="400" y2="120" />
                    </g>
                    <path
                      d="M 10 130 L 70 110 L 130 95 L 190 80 L 250 60 L 310 40 L 380 22 L 380 160 L 10 160 Z"
                      fill="url(#areaGrad)"
                    />
                    <path
                      d="M 10 130 L 70 110 L 130 95 L 190 80 L 250 60 L 310 40 L 380 22"
                      fill="none"
                      stroke="#9fc4a7"
                      strokeWidth="2"
                    />
                    {[
                      [10, 130],
                      [70, 110],
                      [130, 95],
                      [190, 80],
                      [250, 60],
                      [310, 40],
                      [380, 22],
                    ].map(([x, y], i) => (
                      <circle key={i} cx={x} cy={y} r="3.5" fill="#c6dcca" />
                    ))}
                  </svg>
                  <div className="absolute top-0 left-0 text-[11px] text-cream-300/50">900</div>
                  <div className="absolute top-1/2 left-0 text-[11px] text-cream-300/50 -translate-y-1/2">
                    725
                  </div>
                  <div className="absolute bottom-0 left-0 text-[11px] text-cream-300/50">550</div>
                  <div className="absolute bottom-[-22px] inset-x-0 flex justify-between text-[10px] text-cream-300/40 tracking-wider px-2">
                    {['W1', 'W3', 'W5', 'W7', 'W9', 'W11', 'W13'].map((w) => (
                      <span key={w}>{w}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Marker grid */}
          <div className="mt-24 lg:mt-32">
            <p className="eyebrow !text-cream-300 [&::before]:!text-cream-300">Every marker, all at once</p>
            <h2 className="mt-6 font-display text-display-lg text-cream-50 max-w-3xl">
              100+ markers.{' '}
              <em className="italic text-brick-300">One glance.</em>
            </h2>
            <p className="mt-6 text-cream-200/80 text-lg max-w-2xl">
              The marker grid shows where every result lands — in range, above, below,
              or still pending — so you know exactly what to look at first.
            </p>

            <div className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-3 text-[11px] tracking-[0.18em] uppercase text-cream-300/80">
              <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-coral-400"></span>Below range</span>
              <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-sage-400"></span>In range</span>
              <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-amber2-400"></span>Above</span>
            </div>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {MARKER_CATEGORIES.map((c, i) => (
                <div
                  key={c.name}
                  className="bg-charcoal-800/80 border border-charcoal-700 rounded-xl p-4"
                >
                  <div className="flex items-start justify-between">
                    <div className="text-cream-50 text-[15px] leading-tight">{c.name}</div>
                    <div className="text-cream-400 text-[11px] tabular-nums">{c.count}</div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {Array.from({ length: c.count }).map((_, k) => {
                      const palette =
                        (i + k) % 7 === 0
                          ? 'bg-coral-400'
                          : (i + k) % 5 === 0
                          ? 'bg-amber2-400'
                          : 'bg-sage-400/80';
                      return <span key={k} className={`w-3.5 h-3.5 rounded ${palette}`}></span>;
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* UPLOAD PDFs */}
      <section className="py-20 lg:py-28 bg-cream-100">
        <div className="container-wide">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <p className="eyebrow">Bring your own labs</p>
              <h2 className="mt-6 font-display text-display-lg text-ink-900">
                Already have PDFs?{' '}
                <em className="italic text-brick-500">Upload them free.</em>
              </h2>
              <p className="mt-6 text-lg text-ink-600 max-w-md">
                Right now we parse LabCorp PDFs reliably, with more lab formats actively
                being added. Drop your existing draws in and they land on the same
                timeline as anything you order from us.
              </p>
              <Link
                to="/platform"
                className="mt-8 inline-flex items-center gap-2 btn-brick"
              >
                Learn more <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="lg:col-span-7">
              <div className="bg-cream-50 border border-cream-200 rounded-2xl p-6 lg:p-8 shadow-soft">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="bg-cream-100 border border-cream-200 rounded-xl p-4 flex items-center gap-3">
                    <FileText className="w-5 h-5 text-brick-500" />
                    <div className="flex-1">
                      <div className="text-[14px] text-ink-900 font-medium truncate">
                        labcorp_2026_03_14.pdf
                      </div>
                      <div className="text-[11px] text-ink-500 mt-0.5">Uploaded</div>
                    </div>
                    <span className="px-2 py-1 rounded-md bg-sage-100 border border-sage-200 text-[10px] text-sage-700 tracking-wider uppercase">
                      PDF
                    </span>
                  </div>
                  <div className="bg-cream-100 border border-cream-200 rounded-xl p-4 text-[12px] text-ink-500">
                    ↓ Parsed in seconds. Every marker normalized and added to your timeline.
                  </div>
                </div>

                <div className="mt-6 space-y-2.5 text-[14px] text-ink-700">
                  {[
                    ['Total Testosterone', '842 ng/dL'],
                    ['Estradiol, Sensitive', '31 pg/mL'],
                    ['SHBG', '24 nmol/L'],
                    ['ApoB', '89 mg/dL'],
                    ['hs-CRP', '0.70 mg/L'],
                    ['Hemoglobin A1c', '5.2 %'],
                  ].map(([k, v]) => (
                    <div
                      key={k}
                      className="flex items-center justify-between py-2 border-b border-cream-200 last:border-0"
                    >
                      <span className="text-ink-800">{k}</span>
                      <span className="tabular-nums text-ink-900">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BUILD YOUR OWN */}
      <section className="py-20 lg:py-28 bg-cream-50 border-y border-cream-200">
        <div className="container-wide">
          <p className="eyebrow">Build your own</p>
          <h2 className="mt-6 font-display text-display-lg text-ink-900 max-w-4xl">
            Every marker you need.{' '}
            <em className="italic text-brick-500">None that you don&apos;t.</em>
          </h2>
          <p className="mt-5 text-lg text-ink-600 max-w-2xl">
            Never get stuck with bloated panels full of markers you don&apos;t care about.
            Browse 140+ markers with live, lab-verified pricing.
          </p>

          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {BIOMARKERS.slice(0, 8).map((b, i) => {
              const selected = i < 5;
              return (
                <div
                  key={b.id}
                  className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl border transition-all ${
                    selected
                      ? 'bg-sage-50 border-sage-300'
                      : 'bg-cream-50 border-cream-200'
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span
                      className={`w-4 h-4 rounded-[5px] flex items-center justify-center ${
                        selected ? 'bg-sage-500 text-white' : 'bg-cream-100 border border-cream-300'
                      }`}
                    >
                      {selected ? <Check className="w-3 h-3" strokeWidth={3} /> : null}
                    </span>
                    <span className="text-[14px] text-ink-900 truncate">{b.name}</span>
                  </div>
                  <span className="text-[14px] text-ink-500 tabular-nums">${b.price}</span>
                </div>
              );
            })}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6 pt-8 border-t border-cream-200">
            <div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-ink-400">Your panel</div>
              <div className="mt-1 text-ink-700">5 of 8 markers</div>
            </div>
            <div className="font-display text-display-md text-ink-900">
              <span className="text-2xl align-top">$</span>149
            </div>
          </div>

          <p className="mt-3 text-[12px] text-ink-500">
            Sample preview. Browse 140+ markers with live, lab-verified pricing at checkout.
          </p>
          <Link
            to="/biomarkers"
            className="mt-6 inline-flex items-center gap-2 btn-brick"
          >
            Build your own panel <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 lg:py-28 bg-cream-100">
        <div className="container-wide">
          <h2 className="font-display text-display-lg text-ink-900 text-center">
            What people are saying.
          </h2>
        </div>
        {/* Marquee 1 */}
        <div className="mt-12 overflow-hidden">
          <div className="flex gap-5 animate-marquee whitespace-nowrap">
            {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
              <figure
                key={i}
                className="shrink-0 w-[360px] bg-cream-50 border border-cream-200 rounded-2xl p-6"
              >
                <blockquote className="font-display text-[20px] text-ink-900 leading-snug whitespace-normal">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-4 text-[11px] tracking-[0.18em] uppercase text-ink-500">
                  — {t.author}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20 lg:py-28 bg-cream-100 border-t border-cream-200">
        <div className="container-wide">
          <h2 className="font-display text-display-lg text-ink-900 max-w-3xl">
            From order to first result in{' '}
            <em className="italic text-brick-500">under 48 hours</em>.
          </h2>
          <p className="mt-5 text-lg text-ink-600 max-w-2xl">
            Most panels go from checkout to first markers in your dashboard the next day.
          </p>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS_STEPS.map((s) => (
              <div key={s.step} className="relative">
                <div className="text-[11px] tracking-[0.22em] uppercase text-ink-400">{s.step}</div>
                <h3 className="mt-3 font-display text-2xl text-ink-900">{s.title}</h3>
                <p className="mt-3 text-ink-600 leading-relaxed text-[15px]">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-28 lg:py-36 bg-charcoal-900 text-cream-50 overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <svg viewBox="0 0 600 600" className="w-[600px] h-[600px] opacity-40">
            {Array.from({ length: 28 }).map((_, i) => (
              <line
                key={i}
                x1={300 + Math.cos((i / 28) * Math.PI * 2) * 130}
                y1={300 + Math.sin((i / 28) * Math.PI * 2) * 130}
                x2={300 + Math.cos((i / 28) * Math.PI * 2) * 240}
                y2={300 + Math.sin((i / 28) * Math.PI * 2) * 240}
                stroke="#f5ecd9"
                strokeOpacity={0.12 + (i % 4) * 0.04}
                strokeWidth="5"
                strokeLinecap="round"
              />
            ))}
          </svg>
        </div>
        <div className="relative container-wide text-center">
          <h2 className="font-display text-display-xl leading-[0.92]">
            <span className="block">Stop guessing.</span>
            <span className="block italic text-brick-300">Start measuring.</span>
          </h2>
          <Link
            to="/biomarkers"
            className="mt-12 inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-cream-200/60 text-cream-50 hover:bg-cream-50 hover:text-ink-900 transition-all"
          >
            Build your panel <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default HomePage;
